/* Pulse range rail — vanilla port of components/pulse/useRail.ts plus the bits
   RailSection did itself (caption dots, counter, arrow disabling).

   The rail is a native CSS scroll-snap container: the browser owns the
   momentum, the snapping and the interruption. This file only
   (a) reads back which cards are on screen so the dots, counter and edge fade
       can follow, and
   (b) adds what a native scroller lacks with a MOUSE: arrows and grab-drag.

   Reads are throttled to one requestAnimationFrame per scroll burst, and only
   the dots whose state actually changed are touched, so a fast drag does not
   rewrite thirteen elements per frame. */
(function () {
  if (window.__pulse_rail) return;
  window.__pulse_rail = true;

  var REDUCED = '(prefers-reduced-motion: reduce)';

  function pad(n) {
    return n < 10 ? '0' + n : String(n);
  }

  function initRail(root) {
    if (!root || root.__pulseRailBound) return;
    root.__pulseRailBound = true;

    var track = root.querySelector('[data-rail-track]');
    if (!track) return;
    var prev = root.querySelector('[data-rail-prev]');
    var next = root.querySelector('[data-rail-next]');
    var dotsWrap = root.querySelector('[data-rail-dots]');
    var counter = root.querySelector('[data-rail-count]');
    var dots = dotsWrap ? Array.prototype.slice.call(dotsWrap.children) : [];
    var count = track.children.length;
    if (!count) return;

    /* STEP MODE. The lab rail advances one CARD per arrow press, which is right
       when the arrows sit at the rail's own edges and the customer is browsing
       colourways. `data-rail-step="page"` advances one SCREENFUL instead, for
       the homepage rail where the only navigation is a Previous / Next pair in
       a bar above the cards: a text button that shifts the wall by one card
       reads as broken. Page mode also switches the counter from a card range
       ("01-04 / 13") to a page number ("1 / 4"), because that is the unit the
       buttons now move in. Absent, everything behaves exactly as before. */
    var byPage = root.getAttribute('data-rail-step') === 'page';

    /** Cards per screenful, measured LIVE. The card width is a fraction of the
     *  track, so it changes with the viewport and cannot be a constant; and the
     *  gap has to be added to both terms or a 4-up rail measures 3.9 and rounds
     *  to 4 only by luck. */
    function perPage() {
      var first = track.children[0];
      if (!first) return 1;
      var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      var w = first.getBoundingClientRect().width + gap;
      if (!w) return 1;
      return Math.max(1, Math.round((track.clientWidth + gap) / w));
    }

    /* Which cards are ON SCREEN, not which single card is "current". Start
       alignment shows several at a time and the last few share the final frame,
       so a single-active-card model breaks down at the end. A visible RANGE is
       both true and clickable everywhere. */
    var range = [0, 0];
    /* Where the rail sits in its own scroll range. The arrows key off THIS
       rather than off the card index, for the same reason. */
    var edge = 'start';
    var frame = 0;
    /* When the last programmatic scroll was requested, so read() can tell an
       animation in progress from the customer scrolling by hand. */
    var lastGo = 0;
    /* Where the arrows are heading, which is NOT where the rail currently is
       (a smooth scroll takes time; stepping from the observed position makes
       three quick clicks advance one place in total). */
    var target = 0;

    /** Index of whichever card is nearest the rail's LEADING edge, measured
     *  live from the DOM in viewport coordinates (offsetLeft would silently
     *  include an unpositioned ancestor's padding). */
    function nearest() {
      var left = track.getBoundingClientRect().left;
      var best = 0;
      var bestD = Infinity;
      for (var i = 0; i < track.children.length; i++) {
        var d = Math.abs(track.children[i].getBoundingClientRect().left - left);
        if (d < bestD) {
          bestD = d;
          best = i;
        }
      }
      return best;
    }

    /** First and last card at least half on screen. */
    function visible() {
      var rail = track.getBoundingClientRect();
      var first = -1;
      var last = -1;
      for (var i = 0; i < track.children.length; i++) {
        var r = track.children[i].getBoundingClientRect();
        var shown = Math.min(r.right, rail.right) - Math.max(r.left, rail.left);
        if (shown >= r.width / 2) {
          if (first < 0) first = i;
          last = i;
        }
      }
      return first < 0 ? [0, 0] : [first, last];
    }

    function paintEdge(now) {
      if (now === edge) return;
      edge = now;
      /* Fade where the content actually continues, and only there. */
      track.classList.toggle('is-scrolled', edge === 'mid');
      track.classList.toggle('is-end', edge === 'end');
      if (prev) prev.disabled = edge === 'start';
      if (next) next.disabled = edge === 'end';
    }

    function paintRange(now) {
      if (now[0] === range[0] && now[1] === range[1]) return;
      for (var i = 0; i < dots.length; i++) {
        var was = i >= range[0] && i <= range[1];
        var on = i >= now[0] && i <= now[1];
        if (was === on) continue;
        dots[i].classList.toggle('is-on', on);
        if (on) dots[i].setAttribute('aria-current', 'true');
        else dots[i].removeAttribute('aria-current');
      }
      range = now;
      if (counter) {
        if (byPage) {
          var per = perPage();
          var pages = Math.max(1, Math.ceil(count / per));
          var page = Math.min(pages, Math.floor(range[0] / per) + 1);
          counter.textContent = page + ' / ' + pages;
        } else {
          counter.textContent = pad(range[0] + 1) + '–' + pad(range[1] + 1) + ' / ' + count;
        }
      }
    }

    function read() {
      frame = 0;
      paintRange(visible());
      var max = track.scrollWidth - track.clientWidth;
      paintEdge(track.scrollLeft <= 1 ? 'start' : track.scrollLeft >= max - 1 ? 'end' : 'mid');
      // Re-sync the arrows' target to reality, but only once any programmatic
      // scroll has had time to land.
      if (performance.now() - lastGo > 700) target = nearest();
    }

    function onScroll() {
      if (!frame) frame = requestAnimationFrame(read);
    }

    function go(n) {
      var card = track.children[n];
      if (!card) return;
      target = n;
      lastGo = performance.now();
      // Left edge to left edge, matching scroll-snap-align: start. The browser
      // clamps at both ends, so asking for a card that cannot lead parks the
      // rail at its limit.
      var rail = track.getBoundingClientRect();
      var r = card.getBoundingClientRect();
      track.scrollTo({
        left: track.scrollLeft + (r.left - rail.left),
        behavior: window.matchMedia(REDUCED).matches ? 'auto' : 'smooth',
      });
    }

    function step(dir) {
      var by = byPage ? perPage() : 1;
      target = Math.max(0, Math.min(count - 1, target + dir * by));
      go(target);
    }

    /* Drag-to-scroll is owned by assets/pulse-drag-rail.js, which lists this
       track and also suspends scroll-snap and smooth scrolling for the gesture.
       A second writer here fought it on every drag (audit JS-8, 2026-09-03);
       the dots and counter follow the scroll listener below, so nothing is
       lost. Arrows and dots stay here. */

    if (prev) {
      prev.addEventListener('click', function () {
        step(-1);
      });
    }
    if (next) {
      next.addEventListener('click', function () {
        step(1);
      });
    }
    dots.forEach(function (dot, n) {
      dot.addEventListener('click', function () {
        go(n);
      });
    });

    read();
    track.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    /* Theme editor: selecting a shoe block brings its card into view. */
    root.__pulseRailGo = go;
  }

  function initAll(scope) {
    var rails = (scope || document).querySelectorAll('[data-pulse-rail]');
    for (var i = 0; i < rails.length; i++) initRail(rails[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
  document.addEventListener('shopify:block:select', function (e) {
    var root = e.target.closest ? e.target.closest('[data-pulse-rail]') : null;
    if (!root || !root.__pulseRailGo) return;
    var track = root.querySelector('[data-rail-track]');
    if (!track) return;
    var n = Array.prototype.indexOf.call(track.children, e.target);
    if (n >= 0) root.__pulseRailGo(n);
  });
})();
